import React, { useRef, useState } from 'react'
import { Grid, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'

import i18n from '../../locales/i18n'
import { Nil } from '../../types'
import ButtonWithLoader from '../buttons/ButtonWithLoader'
import SignatureDrawer, { SignatureDrawerHandle } from './SignatureDrawer'

const useStyles = makeStyles(
  theme => ({
    item: {
      width: '100%',
      padding: theme.spacing(0, 2),
      marginBottom: theme.spacing(2),
    },
    headerText: {
      fontSize: '2rem',
      color: theme.colors.primaryText,
      fontWeight: 500,
    },
    button: {
      width: 152,
      height: 40,
      [theme.breakpoints.down('sm')]: {
        width: '100%',
      },
    },
    buttonContainer: {
      '&:not(:last-of-type)': {
        marginRight: theme.spacing(1.5),
      },
    },
  }),
  { name: 'Signature' },
)

export interface SignatureProps {
  readonly initialSignatureUrl?: string | Nil
  readonly isLoading?: boolean
  readonly onSave: (value: string) => void
  readonly outputFormat?: 'png' | 'svg'
}

const Signature = ({
  initialSignatureUrl,
  isLoading,
  outputFormat = 'svg',
  onSave,
}: SignatureProps) => {
  const classes = useStyles()
  const drawerRef = useRef<SignatureDrawerHandle>(null)

  const [hasChanges, setHasChanges] = useState(false)

  const handleSave = () => {
    if (!drawerRef.current?.validate?.()) {
      return
    }

    const formatter =
      outputFormat === 'svg'
        ? drawerRef.current.getSvg
        : drawerRef.current.getPng

    onSave(formatter())
  }

  const handleClear = () => {
    drawerRef.current?.clear()
    setHasChanges(false)
  }

  const handleBeginStroke = () => {
    if (!hasChanges) {
      setHasChanges(true)
    }
  }

  return (
    <Grid container direction="column">
      <Grid item className={classes.item}>
        <Typography className={classes.headerText}>
          {i18n.t<string>('Common:SIGNATURE.PLEASE_SIGN_BELOW')}
        </Typography>
      </Grid>
      <Grid item className={classes.item}>
        <SignatureDrawer
          disabled={isLoading}
          initialDataUrl={initialSignatureUrl}
          ref={drawerRef}
          onBeginStroke={handleBeginStroke}
        />
      </Grid>
      <Grid
        container
        item
        className={classes.item}
        justifyContent="space-between"
        wrap="nowrap"
      >
        <Grid item xs className={classes.buttonContainer} sm="auto">
          <ButtonWithLoader
            className={classes.button}
            color="secondary"
            loading={isLoading}
            onClick={handleClear}
          >
            {i18n.t<string>('Common:CLEAR_ACTION')}
          </ButtonWithLoader>
        </Grid>
        <Grid item xs className={classes.buttonContainer} sm="auto">
          <ButtonWithLoader
            className={classes.button}
            disabled={!hasChanges}
            loading={isLoading}
            onClick={handleSave}
          >
            {i18n.t<string>('Common:SIGNATURE.SAVE_ACTION')}
          </ButtonWithLoader>
        </Grid>
      </Grid>
    </Grid>
  )
}

export default Signature
